import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { THEME } from '../../styles/theme';

// ── Grade badge colours ────────────────────────────────────────────────
const GRADE_COLORS = {
  'A+': { color: THEME.colors.gradeAP, bg: 'rgba(0,255,136,0.12)' },
  'A':  { color: THEME.colors.gradeA,  bg: 'rgba(0,212,255,0.12)' },
  'B':  { color: THEME.colors.gradeB,  bg: 'rgba(255,170,0,0.12)' },
  'C':  { color: THEME.colors.gradeC,  bg: 'rgba(255,102,0,0.12)' },
  'D':  { color: THEME.colors.gradeD,  bg: 'rgba(255,51,51,0.12)' },
};
const DIR_COLORS = { BUY: THEME.colors.green, SELL: THEME.colors.red, WAIT: THEME.colors.gold };

// ── ReactMarkdown component overrides for JARVIS dark theme ───────────
const MD_COMPONENTS = {
  p:      ({ children }) => (
    <p style={{ fontSize: '14px', color: THEME.colors.text, lineHeight: '1.7', margin: '4px 0' }}>
      {children}
    </p>
  ),
  strong: ({ children }) => (
    <strong style={{ color: THEME.colors.blue, fontWeight: '700' }}>{children}</strong>
  ),
  em:     ({ children }) => (
    <em style={{ color: THEME.colors.gold, fontStyle: 'italic' }}>{children}</em>
  ),
  h1:     ({ children }) => (
    <div style={{ fontSize: '15px', fontWeight: '800', color: THEME.colors.blue, letterSpacing: '0.5px', margin: '14px 0 6px', fontFamily: THEME.fonts.hud }}>
      {children}
    </div>
  ),
  h2:     ({ children }) => (
    <div style={{ fontSize: '14px', fontWeight: '700', color: THEME.colors.blue, letterSpacing: '0.5px', margin: '12px 0 5px', fontFamily: THEME.fonts.hud }}>
      {children}
    </div>
  ),
  h3:     ({ children }) => (
    <div style={{ fontSize: '13px', fontWeight: '700', color: THEME.colors.gold, letterSpacing: '0.5px', margin: '10px 0 4px', fontFamily: THEME.fonts.hud }}>
      {children}
    </div>
  ),
  ul:     ({ children }) => (
    <ul style={{ margin: '6px 0', paddingLeft: '18px', listStyle: 'none' }}>{children}</ul>
  ),
  ol:     ({ children }) => (
    <ol style={{ margin: '6px 0', paddingLeft: '20px', color: THEME.colors.text }}>{children}</ol>
  ),
  li:     ({ children }) => (
    <li style={{ fontSize: '14px', color: THEME.colors.text, lineHeight: '1.6', padding: '2px 0', display: 'flex', gap: '6px' }}>
      <span style={{ color: THEME.colors.blueMid, flexShrink: 0 }}>›</span>
      <span>{children}</span>
    </li>
  ),
  code:   ({ inline, children }) => inline
    ? (
      <code style={{
        background:   'rgba(0,212,255,0.1)',
        border:       '1px solid rgba(0,212,255,0.2)',
        borderRadius: '4px',
        padding:      '1px 6px',
        fontSize:     '13px',
        color:        THEME.colors.blue,
        fontFamily:   'monospace',
      }}>
        {children}
      </code>
    )
    : (
      <pre style={{
        background:   'rgba(0,0,0,0.4)',
        border:       '1px solid rgba(0,212,255,0.15)',
        borderRadius: '8px',
        padding:      '12px',
        overflow:     'auto',
        margin:       '8px 0',
      }}>
        <code style={{ fontSize: '13px', color: '#a8d8ff', fontFamily: 'monospace', lineHeight: '1.6' }}>
          {children}
        </code>
      </pre>
    ),
  hr:     () => (
    <div style={{ height: '1px', background: 'rgba(0,212,255,0.1)', margin: '12px 0' }} />
  ),
  blockquote: ({ children }) => (
    <div style={{
      borderLeft:  `3px solid ${THEME.colors.gold}`,
      paddingLeft: '12px',
      margin:      '8px 0',
      color:       THEME.colors.textDim,
      fontStyle:   'italic',
    }}>
      {children}
    </div>
  ),
};

// ── Trade badge row — extracted from JARVIS structured responses ────────
function TradeBadges({ text }) {
  const gradeMatch = text.match(/GRADE\s*[:：]\s*\[?([A-D+]+)\]?/i);
  const dirMatch   = text.match(/RECOMMENDATION\s*[:：]\s*\[?(BUY|SELL|WAIT)\b/i);
  const confMatch  = text.match(/JARVIS\s+CONFIDENCE\s*[:：]\s*(\d+)%/i);
  const grade      = gradeMatch?.[1]?.trim().toUpperCase();
  const direction  = dirMatch?.[1]?.trim().toUpperCase();
  const confidence = confMatch ? parseInt(confMatch[1], 10) : null;

  if (!grade && !direction) return null;

  const gradeStyle = GRADE_COLORS[grade] || {};
  return (
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
      {grade && (
        <span style={{
          padding: '4px 12px', borderRadius: '20px', fontSize: '13px',
          fontWeight: '800', background: gradeStyle.bg, color: gradeStyle.color,
          border: `1px solid ${gradeStyle.color}44`, fontFamily: THEME.fonts.hud, letterSpacing: '1px',
        }}>
          GRADE {grade}
        </span>
      )}
      {direction && (
        <span style={{
          padding: '4px 14px', borderRadius: '20px', fontSize: '13px',
          fontWeight: '800', background: `${DIR_COLORS[direction]}18`, color: DIR_COLORS[direction],
          border: `1px solid ${DIR_COLORS[direction]}44`, fontFamily: THEME.fonts.hud, letterSpacing: '1px',
        }}>
          {direction === 'BUY' ? '▲' : direction === 'SELL' ? '▼' : '◎'} {direction}
        </span>
      )}
      {confidence !== null && (
        <span style={{
          padding: '4px 12px', borderRadius: '20px', fontSize: '12px',
          fontWeight: '700', background: 'rgba(255,255,255,0.06)', color: THEME.colors.textDim,
          border: '1px solid rgba(255,255,255,0.1)', fontFamily: THEME.fonts.hud,
        }}>
          {confidence}% CONFIDENCE
        </span>
      )}
    </div>
  );
}

// ── Single message bubble ─────────────────────────────────────────────
export default function MessageBubble({ message }) {
  const [copied, setCopied] = useState(false);
  const isUser    = message.role === 'user';
  const isError   = message.isError;
  const timestamp = message.timestamp
    ? new Date(message.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })
    : '';

  const handleCopy = async () => {
    await navigator.clipboard.writeText(message.content).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (isUser) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px', paddingLeft: '60px' }}>
        <div style={{
          maxWidth: '70%', background: 'rgba(0,212,255,0.1)',
          border: '1px solid rgba(0,212,255,0.25)', borderRadius: '16px 4px 16px 16px', padding: '12px 16px',
        }}>
          {message.screenshot && (
            <div style={{ marginBottom: '8px' }}>
              <div style={{ fontSize: '11px', color: THEME.colors.gold, letterSpacing: '1px', fontFamily: THEME.fonts.hud }}>
                📸 CHART SCREENSHOT
              </div>
              <div style={{ fontSize: '10px', color: THEME.colors.green, letterSpacing: '1px', marginTop: '2px', fontFamily: THEME.fonts.hud }}>
                ✓ READ BY JARVIS — DELETED FROM MEMORY
              </div>
            </div>
          )}
          <p style={{ fontSize: '14px', color: THEME.colors.text, lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
            {message.content}
          </p>
          <div style={{ fontSize: '11px', color: THEME.colors.textMuted, marginTop: '6px', textAlign: 'right' }}>
            {timestamp}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', paddingRight: '40px' }}>
      {/* JARVIS avatar */}
      <div style={{
        width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0, marginTop: '2px',
        background: isError ? 'rgba(255,51,51,0.15)' : 'rgba(0,212,255,0.1)',
        border: `2px solid ${isError ? THEME.colors.red : THEME.colors.blue}44`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px',
      }}>
        🤖
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Name + time */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <span style={{
            fontSize: '12px', fontWeight: '700', letterSpacing: '1px', fontFamily: THEME.fonts.hud,
            color: isError ? THEME.colors.red : THEME.colors.blue,
          }}>
            JARVIS
          </span>
          <span style={{ fontSize: '11px', color: THEME.colors.textMuted }}>{timestamp}</span>
        </div>

        {/* Message body */}
        <div style={{
          background: isError ? 'rgba(255,51,51,0.06)' : 'rgba(13,21,38,0.8)',
          border: `1px solid ${isError ? 'rgba(255,51,51,0.2)' : 'rgba(0,212,255,0.1)'}`,
          borderRadius: '4px 16px 16px 16px', padding: '16px', position: 'relative',
        }}>
          {/* Trade analysis badges */}
          <TradeBadges text={message.content} />

          {/* Render content with ReactMarkdown */}
          <ReactMarkdown components={MD_COMPONENTS}>
            {message.content}
          </ReactMarkdown>

          {/* Copy button */}
          {!isError && (
            <button
              onClick={handleCopy}
              style={{
                position: 'absolute', top: '10px', right: '10px',
                background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', padding: '2px 6px',
                color: copied ? THEME.colors.green : 'rgba(255,255,255,0.2)',
              }}
              title="Copy response"
            >
              {copied ? '✓' : '⎘'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
